import React, { useState } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { UploadIcon, FileTextIcon, CheckIcon, XIcon, InfoIcon, AlertTriangleIcon } from 'lucide-react';
interface BulkEmployeeImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (employees: any[]) => void;
}
export function BulkEmployeeImportModal({
  isOpen,
  onClose,
  onImport
}: BulkEmployeeImportModalProps) {
  // State for import steps
  const [step, setStep] = useState(1);
  const [file, setFile] = useState<File | null>(null);
  const [parsedData, setParsedData] = useState<any[]>([]);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  // Handle file upload
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const uploadedFile = e.target.files[0];
      setFile(uploadedFile);
      // Simulate parsing the file
      setTimeout(() => {
        parseFile(uploadedFile);
      }, 500);
    }
  };
  // Parse the uploaded file
  const parseFile = (file: File) => {
    setIsProcessing(true);
    // Simulate file parsing (in a real app, you'd parse CSV/Excel)
    setTimeout(() => {
      // Mock parsed data
      const mockParsedData = [{
        name: 'Alex Thompson',
        email: 'alex.thompson@example.com',
        phone: '555-111-2222',
        department: 'Engineering',
        position: 'Frontend Developer',
        role: 'employee'
      }, {
        name: 'Morgan Lee',
        email: 'morgan.lee@example.com',
        phone: '555-333-4444',
        department: 'Product',
        position: 'Product Manager',
        role: 'employee'
      }, {
        name: 'Casey Rivera',
        email: 'casey.rivera@example.com',
        phone: '555-555-6666',
        department: 'Marketing',
        position: 'Content Strategist',
        role: 'employee'
      }];
      // Mock validation errors
      const mockErrors = ['Row 4: Missing email address', 'Row 6: Invalid phone number format'];
      setParsedData(mockParsedData);
      setValidationErrors(mockErrors);
      setIsProcessing(false);
      setStep(2);
    }, 1500);
  };
  // Handle import confirmation
  const handleConfirmImport = () => {
    setIsProcessing(true);
    // Simulate import process
    setTimeout(() => {
      onImport(parsedData);
      resetModal();
      onClose();
    }, 1000);
  };
  // Reset modal state
  const resetModal = () => {
    setStep(1);
    setFile(null);
    setParsedData([]);
    setValidationErrors([]);
    setIsProcessing(false);
  };
  // Handle modal close
  const handleClose = () => {
    resetModal();
    onClose();
  };
  return <Modal isOpen={isOpen} onClose={handleClose} title="Bulk Import Employees" size="lg" data-id="element-518">
      {step === 1 && <div className="p-6" data-id="element-519">
          <div className="mb-6" data-id="element-520">
            <p className="text-gray-300 mb-4" data-id="element-521">
              Upload a CSV or Excel file containing employee information to
              import multiple employees at once.
            </p>
            <div className="bg-blue-900/20 border border-blue-800 rounded-md p-4 flex items-start" data-id="element-522">
              <InfoIcon className="h-5 w-5 text-blue-400 mt-0.5 mr-3 flex-shrink-0" data-id="element-523" />
              <div data-id="element-524">
                <p className="text-sm text-blue-300" data-id="element-525">
                  Your file should include the following columns:
                </p>
                <ul className="mt-1 text-xs text-blue-200 list-disc list-inside" data-id="element-526">
                  <li data-id="element-527">Name (required)</li>
                  <li data-id="element-528">Email (required)</li>
                  <li data-id="element-529">Phone</li>
                  <li data-id="element-530">Department (required)</li>
                  <li data-id="element-531">Position (required)</li>
                  <li data-id="element-532">Role (defaults to "employee" if not specified)</li>
                </ul>
              </div>
            </div>
          </div>
          <div className="border-2 border-dashed border-gray-600 rounded-lg p-6 text-center bg-gray-700" data-id="element-533">
            {file ? <div className="flex flex-col items-center" data-id="element-534">
                <FileTextIcon className="h-10 w-10 text-blue-400 mb-2" data-id="element-535" />
                <p className="text-gray-200 mb-1" data-id="element-536">{file.name}</p>
                <p className="text-xs text-gray-400" data-id="element-537">
                  {(file.size / 1024).toFixed(2)} KB
                </p>
                {isProcessing ? <div className="mt-4 flex items-center text-gray-300" data-id="element-538">
                    <div className="animate-spin mr-2 h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full" data-id="element-539"></div>
                    Processing file...
                  </div> : <button onClick={() => setFile(null)} className="mt-4 text-sm text-red-400 hover:text-red-300" data-id="element-540">
                    Remove file
                  </button>}
              </div> : <div className="flex flex-col items-center" data-id="element-541">
                <UploadIcon className="h-10 w-10 text-gray-500" data-id="element-542" />
                <p className="mt-2 text-sm text-gray-300" data-id="element-543">
                  Drag and drop your file, or{' '}
                  <label className="text-blue-400 hover:text-blue-300 cursor-pointer" data-id="element-544">
                    browse
                    <input type="file" className="hidden" accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel" onChange={handleFileUpload} data-id="element-545" />
                  </label>
                </p>
                <p className="mt-1 text-xs text-gray-400" data-id="element-546">
                  CSV or Excel files up to 5MB
                </p>
              </div>}
          </div>
        </div>}
      {step === 2 && <div className="p-6" data-id="element-547">
          <div className="mb-6" data-id="element-548">
            <p className="text-gray-300 mb-4" data-id="element-549">
              Review the employee data before importing. We found{' '}
              {parsedData.length} valid employees to import.
            </p>
            {validationErrors.length > 0 && <div className="bg-yellow-900/20 border border-yellow-800 rounded-md p-4 flex items-start mb-4" data-id="element-550">
                <AlertTriangleIcon className="h-5 w-5 text-yellow-400 mt-0.5 mr-3 flex-shrink-0" data-id="element-551" />
                <div data-id="element-552">
                  <p className="text-sm text-yellow-300" data-id="element-553">
                    We found {validationErrors.length} issues in your file:
                  </p>
                  <ul className="mt-1 text-xs text-yellow-200 list-disc list-inside" data-id="element-554">
                    {validationErrors.map((error, index) => <li key={index} data-id="element-555">{error}</li>)}
                  </ul>
                  <p className="mt-2 text-xs text-yellow-300" data-id="element-556">
                    These rows will be skipped during import.
                  </p>
                </div>
              </div>}
          </div>
          <div className="border border-gray-700 rounded-lg overflow-hidden mb-4" data-id="element-557">
            <table className="min-w-full divide-y divide-gray-700" data-id="element-558">
              <thead className="bg-gray-750" data-id="element-559">
                <tr data-id="element-560">
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider" data-id="element-561">
                    Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider" data-id="element-562">
                    Email
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider" data-id="element-563">
                    Department
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider" data-id="element-564">
                    Position
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider" data-id="element-565">
                    Role
                  </th>
                </tr>
              </thead>
              <tbody className="bg-gray-800 divide-y divide-gray-700" data-id="element-566">
                {parsedData.map((employee, index) => <tr key={index} className="hover:bg-gray-750" data-id="element-567">
                    <td className="px-4 py-3 text-sm text-gray-200" data-id="element-568">
                      {employee.name}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-200" data-id="element-569">
                      {employee.email}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-200" data-id="element-570">
                      {employee.department}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-200" data-id="element-571">
                      {employee.position}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-200 capitalize" data-id="element-572">
                      {employee.role}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>}
      <div className="bg-gray-750 px-6 py-4 flex justify-between border-t border-gray-700" data-id="element-573">
        {step === 1 ? <>
            <Button variant="outline" onClick={handleClose} data-id="element-574">
              Cancel
            </Button>
            <Button disabled={!file || isProcessing} onClick={() => {
          // This is handled by the file upload process
        }} data-id="element-575">
              {isProcessing ? <>
                  <div className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full" data-id="element-576"></div>
                  Processing...
                </> : <>Upload & Continue</>}
            </Button>
          </> : <>
            <Button variant="outline" onClick={() => {
          setStep(1);
          setIsProcessing(false);
        }} data-id="element-577">
              <XIcon size={16} className="mr-1.5" data-id="element-578" />
              Back
            </Button>
            <Button onClick={handleConfirmImport} disabled={isProcessing} data-id="element-579">
              {isProcessing ? <>
                  <div className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full" data-id="element-580"></div>
                  Importing...
                </> : <>
                  <CheckIcon size={16} className="mr-1.5" data-id="element-581" />
                  Confirm Import
                </>}
            </Button>
          </>}
      </div>
    </Modal>;
}